import React, { createContext, useState, useEffect } from 'react';
import { db } from './Firebase';
import i18n from './i18n';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  onSnapshot
} from 'firebase/firestore';

export const AppContext = createContext();

export const DEFAULT_STATS = [
  { id: 'stat-1', target: 500, suffix: '+', label: 'Silver Designs', labelMr: 'सिल्व्हर डिझाईन्स', icon: 'Gem' },
  { id: 'stat-2', target: 15, suffix: '+', label: 'Years of Craft', labelMr: 'कारागिरीची वर्षे', icon: 'Clock' },
  { id: 'stat-3', target: 1000, suffix: '+', label: 'Happy Clients', labelMr: 'आनंदी ग्राहक', icon: 'Users' },
  { id: 'stat-4', target: 100, suffix: '%', label: 'Pure Silver', labelMr: 'शुद्ध चांदी', icon: 'Award' },
];

// Dictionary for Marathi & English Translations
export const TRANSLATIONS = {
  en: {
    // Nav
    home: "Home",
    aboutUs: "About Us",
    products: "Products",
    giftsByOccasion: "Gifts by Occasion",
    blog: "Blog",
    contactUs: "Contact Us",
    searchPlaceholder: "Search jewellery...",
    cartTitle: "Shopping Cart",
    wishlistTitle: "Saved Items",
    emptyCart: "Your cart is empty",
    emptyWishlist: "Your wishlist is empty",
    subtotal: "Subtotal",
    checkoutBtn: "Proceed to Inquiry",
    clearCart: "Clear Cart",
    buyNowTitle: "Direct Purchase Inquiry",

    // Products & Filters
    filters: "FILTERS",
    clearAll: "Clear All",
    ringSize: "Ring Size",
    priceRange: "Price Range",
    productType: "Product Type",
    weightRange: "Weight Range",
    addToCart: "Add to Cart",
    buyNow: "Buy Now",
    quickView: "Quick View",
    availableSizes: "Available Sizes:",
    quantity: "Quantity",
    samePattern: "Same Pattern Products",

    // Gifting
    giftsForHer: "Gifts For Her",
    giftsForHim: "Gifts For Him",
    giftsForBaby: "Gifts For Baby & Kids",
    whoShoppingFor: "Who Are You Shopping For?",
    giftCuratorTitle: "Try Our Gift Curator!",
    giftCuratorSub: "Answer 3 simple questions and we'll find the perfect jewellery gift for you.",
    giftByOccasion: "Gift by Occasion",
    giftingByRelation: "GIFTING BY RELATION",
    whoAreYouShoppingFor: "Who Are You Shopping For?",
    selectRecipient: "Select a recipient to explore curated gift collections crafted just for them",
    giftEternity: "Give the Gift of Eternity",
    giftEternityDesc: "Curated silver jewellery — turning every occasion into a lifetime memory",
    giftsOccasionTag: "✦ GIFTS BY OCCASION & RELATION ✦",
    specialMoments: "SPECIAL MOMENTS",
    giftByOccasionLabel: "Gift by Occasion",
    giftOccasionDesc: "Handpicked collections for life's most precious milestones",
    viewGifts: "View Gifts",
    exploreArrow: "Explore →",
    notSureWhat: "Not Sure What To Buy?",
    giftCuratorHeading: "Try Our Gift Curator!",
    iAmLooking: "I am looking for exquisite",
    chooseCategory: "Choose Category",
    jewellery: "jewellery,",
    within: "within",
    choosePriceRange: "Choose Price Range",
    under5k: "Under ₹5,000",
    range5k20k: "₹5,000 – ₹20,000",
    over20k: "Over ₹20,000",
    forWord: "for",
    chooseRelation: "Choose Relation",
    continueBtn: "Continue",
    signaturePackaging: "Signature Packaging Included Free",
    silverSpecial: "Silver Special",
    silverSignature: "Silver Signature",

    // Banner text
    gdrFineJewellery: "GDR Fine Jewellery",
    exquisiteCollection: "Our Exquisite Collection",
    bannerDesc: "Handcrafted masterpieces curated for enduring elegance",

    // Video titles
    royalCraftsmanship: "Royal Craftsmanship",
    handcraftedHeritage: "Handcrafted Heritage",
    silverPureElegance: "Silver Pure Elegance",

    // Blog page
    gdrLuxuryEditorial: "GDR LUXURY EDITORIAL",
    theGdrJournal: "THE GDR JOURNAL & EDITORIAL",
    jewelleryStories: "Jewellery Stories & Heritage Style",
    exploreCraftsmanship: "Explore master craftsmanship, silver care guides, and the latest trends in fine jewellery",
    backToJournal: "Back to Journal",
    enjoyedThisArticle: "Enjoyed this GDR Journal Article?",
    subscribeText: "Subscribe to receive exclusive preview invites to private jewellery launches and styling guides.",
    enterEmail: "Enter your email address...",
    subscribeBtn: "Subscribe",
    readFullArticle: "Read Full Article →",

    // Admin
    adminTitle: "GDR Control Center",
    dashboard: "Dashboard",
    totalProducts: "Total Products",
    giftItems: "Gift Items",
    articles: "Articles",
    customerInquiries: "Customer Inquiries",
    addProduct: "Add New Product",
    addGift: "Add New Gift",
    addArticle: "Publish Article",
    selectLanguage: "Language / भाषा",
    handcraftedHeritageAdmin: "Handcrafted Heritage",
    representingPurity: "Representing the purity of 92.5 Fine Silver and 22K Royal Gold collections.",
    editProduct: "Edit Product",
    editGift: "Edit Gift",
    editBlog: "Edit Blog",
    autoTranslate: "Auto Translate",
    productNameEn: "Product Name (English)",
    productNameMr: "Product Name (Marathi)",
    descriptionEn: "Description (English)",
    descriptionMr: "Description (Marathi)",
    saveChanges: "Save Changes",
    cancel: "Cancel",
    translationsMr: "Translations (Marathi)",

    // Hero Banner
    heroBannerTag1: "✦ EXCLUSIVE WOMEN'S COLLECTION ✦",
    heroBannerH1: "Timeless Silver",
    heroBannerH2: "Necklaces & Pendants",
    heroBannerSub1: "Celebrate every special moment with handcrafted 925 sterling silver necklaces, statement pendants, and bridal neckwear made for timeless grace.",
    heroBannerAccent1: "Necklaces & Pendants",
    heroBannerCta1: "Explore Necklaces",
    heroBannerTag2: "✦ MAHARASHTRA'S HERITAGE ARTISTRY ✦",
    heroBannerH3: "Heritage 925",
    heroBannerH4: "Silver Payal & Anklets",
    heroBannerSub2: "Discover our premier wholesale range of authentic silver payals, crafted with enduring shine, pure hallmark quality, and traditional motifs.",
    heroBannerAccent2: "Payal & Anklets",
    heroBannerCta2: "Explore Payal Collection",
    heroBannerTag3: "✦ MEN'S LUXURY SIGNATURE ✦",
    heroBannerH5: "Men's Silver",
    heroBannerH6: "Bracelets & Kadas",
    heroBannerSub3: "Bold, distinguished, and built to last. Premium solid 925 sterling silver bracelets and designer kadas tailored for prestige and modern charisma.",
    heroBannerAccent3: "Men's Jewellery",
    heroBannerCta3: "Explore Men's Collection",
    enquireWholesale: "Enquire Wholesale",
    scrollToExplore: "Scroll to Explore",

    // Marquee strip
    marqueeItems: [
      "✦ Pure Silver", "✦ Wholesale", "✦ Payal", "✦ Bracelets", "✦ Chains",
      "✦ Rings", "✦ Men's Collection", "✦ Waist Chains", "✦ Premium Quality",
      "✦ Best Prices", "✦ GDR Jewellers"
    ],

    // Stats Section
    stat1Label: "Silver Designs",
    stat2Label: "Years of Craft",
    stat3Label: "Happy Clients",
    stat4Label: "Pure Silver",

    // Occasion Section
    occasionEyebrow: "✦ CELEBRATE EVERY OCCASION WITH GDR JEWELLERS ✦",
    occasionTitle: "Jewellery For",
    occasionTitleItalic: "Every Special Occasion",
    occasionSub: "Discover timeless Silver jewellery for Weddings, Birthdays, Anniversaries, Engagements, Baby Showers and Festivals, thoughtfully crafted for every special occasion.",
    occasionEngagement: "Engagement",
    occasionValentines: "Valentine's Day",
    occasionSangeet: "Sangeet",
    occasionReception: "Reception",
    occasionWedding: "Wedding Day",
    occasionBridal: "Bridal Collection",

    // Look Section
    lookEyebrow: "✦ DISCOVER YOUR SIGNATURE SILVER STYLE ✦",
    lookTitle: "Choose Your",
    lookTitleItalic: "Perfect Look",
    lookSub: "Explore handcrafted 925 Silver jewellery curated for Classic, Modern, Office, Party and Evening styles, designed to complement every personality.",
    shopTheLook: "Shop The Look",
    lookCasual: "Casual Look",
    lookCasualDesc: "Minimalist silver chains and everyday studs.",
    lookOffice: "Office Look",
    lookOfficeDesc: "Sophisticated cuff bracelets and understated elegance.",
    lookModern: "Modern Look",
    lookModernDesc: "Trendy geometric shapes and stackable gemstone rings.",
    lookClassic: "Classic Look",
    lookClassicDesc: "Timeless solitaire diamonds and heritage gold designs.",
    lookParty: "Party Look",
    lookPartyDesc: "Vibrant gemstone earrings and statement accessories.",
    lookEvening: "Evening Look",
    lookEveningDesc: "Dazzling necklaces and chandelier drop earrings.",

    // New Luxury Section
    luxurySpecialGiftBox: "Special Gift Box",
    luxuryPure925: "Pure 925 Silver",
    luxurySilverIdols: "Silver Idols & Divine Artefacts",
    luxurySilverIdolsDesc: "Handcrafted silver idols for home temple & auspicious gifting",
    luxuryExploreCollection: "Explore Collection",
    luxuryNewLaunch: "New Launch",
    luxuryNewArrival: "New Arrival",
    luxuryMangalsutra: "Station Mangalsutra & Royal Chains",
    luxuryMangalsutraDesc: "Modern minimal 22K gold mangalsutras designed for daily elegance",
    luxuryShopNewArrivals: "Shop New Arrivals",
    luxuryGdrSignatureEdit: "GDR SIGNATURE EDIT",
    luxuryGiftsSparkle: "Gifts that Sparkle Beyond the Moment",
    luxuryGiftsDesc: "Celebrate endless love for the ones you cherish most with exquisite handcrafted jewels from GDR.",
    luxuryExploreGifting: "Explore GDR's Gifting Edit",
    luxuryForHer: "For Her",
    luxuryForHerDesc: "Solitaire Rings & Chains",
    luxuryForHim: "For Him",
    luxuryForHimDesc: "Kada & Cuban Links",
    luxuryForKids: "For Kids",
    luxuryForKidsDesc: "Tender Painjans & Nazariya",

    // About Section (Home)
    aboutLegacyOfTrust: "✦ Legacy of Trust ✦",
    aboutTitle: "About",
    aboutTitleItalic: "GDR",
    aboutTitleSuffix: "Jewellers",
    aboutP1: "GDR Jewellers is a premier name in the wholesale silver jewelry market. We specialize in providing high-quality, pure 925 silver ornaments to retailers and wholesalers across the region.",
    aboutP2: "Our curated collections span exquisite Payal, elegant bracelets, solid men's chains, and traditional waist ornaments. Built on the pillars of purity, artistry, and trust, we guarantee the finest hallmark quality.",
    aboutFeature1Title: "BIS 925 Hallmarked",
    aboutFeature1Desc: "Unmatched purity guarantee on every product.",
    aboutFeature2Title: "Wholesale Competitive Rates",
    aboutFeature2Desc: "Optimized pricing structures for bulk orders.",
    aboutFeature3Title: "Pan-India Shipping",
    aboutFeature3Desc: "Safe, secure, and fast delivery networks.",
    aboutEst: "Est. 2011",

    // Footer
    footerPageHeading: "Showroom Pages",
    footerGiftingHeading: "Gifting Collections",
    footerInfoHeading: "Showroom Info",
    footerHomeLink: "Home",
    footerAboutLink: "About Us",
    footerProductsLink: "All Products",
    footerGiftingLink: "Gifts by Occasion",
    footerBlogLink: "Blog & Editorial",
    footerContactLink: "Contact Us",
    footerGiftHer: "Gifts For Her",
    footerGiftHim: "Gifts For Him",
    footerGiftBaby: "For Baby & Kids",
    footerGiftBirthday: "Birthday Gifts",
    footerGiftAnniversary: "Anniversary Gifts",
    footerGiftWedding: "Wedding Collection",
    footerGiftValentines: "Valentine's Day",
    footerAddress: "GDR House, Hupari, Kolhapur, Maharashtra",
    footerPhone1: "+91 91724 55555",
    footerPhone2: "+91 251 2444321",
    footerEmail: "gdrjwell@gmail.com",
    footerTerms: "Terms & Conditions",
    footerFaq: "FAQ & Sizing",
    footerBrandDesc: "Exquisite BIS Hallmarked 22K Gold & 925 Sterling Silver Jewellery. Handcrafted traditions trusted across India.",
    footerCopyright: "GDR Jewellers. All Rights Reserved.",
    footerHallmark: "BIS Hallmarked Gold & 925 Sterling Silver Masterpieces",

    // About Page
    aboutPageEyebrow: "Crafting Legacies Since 1995",
    aboutPageH1Line1: "Where Tradition Meets",
    aboutPageH1Line2: "Timeless Elegance",
    aboutPageHeroSub: "Three decades of crafting India's most treasured family heirlooms in certified gold and pure silver.",
    aboutPageScrollCta: "Discover Our Story",
    aboutPageOurStory: "OUR STORY",
    aboutPageHeritageTitle: "A Heritage of",
    aboutPageHeritageTitleItalic: "Gold & Silver",
    aboutPageHeritageP1: "Founded in 1995 by master craftsman Govind Devram Rane, GDR Jewellers began as a small workshop in Maharashtra with one vision: to create jewellery that lasts generations. Today, it's a celebrated name across India for purity, artistry, and bespoke design.",
    aboutPageHeritageP2: "Each piece that leaves our workshop carries the weight of three decades of expertise, the precision of modern CAD design, and the warmth of handcrafted tradition. We are more than a jeweller — we are keepers of your most treasured memories.",
    aboutPageOurJourney: "OUR JOURNEY",
    aboutPageMilestones: "Milestones of Excellence",
    aboutPageOurValues: "OUR VALUES",
    aboutPageValuesTitle: "What We Stand For",
    aboutPageCraftTitle: "The Art of Creation",
    aboutPageCraftSub: "Every GDR piece is born through a meticulous 5-step handcrafting journey",
    aboutPageTeamTitle: "OUR MASTER CRAFTSMEN",
    aboutPageTeamSub: "The Heritage Keepers",
    aboutPageCtaEyebrow: "JOIN THE GDR FAMILY",
    aboutPageCtaTitle: "Begin Your Jewellery Journey",
    aboutPageCtaSub: "Visit our showroom or request a personalised consultation. Your dream jewellery is one conversation away.",
    aboutPageCtaBtn1: "Visit Our Showroom",
    aboutPageCtaBtn2: "Book Consultation",
    milestone1Year: "1995", milestone1Title: "The Genesis", milestone1Desc: "Govind Rane founded GDR Jewellers in Maharashtra with a single workbench and a vision for purest gold craftsmanship.",
    milestone2Year: "2005", milestone2Title: "Legacy Expansion", milestone2Desc: "Opened our flagship showroom specializing in custom handcrafted temple jewellery and pure silver payals.",
    milestone3Year: "2015", milestone3Title: "Digital Era", milestone3Desc: "Introduced 3D jewellery CAD designing, allowing clients to visualize their bespoke creations before crafting begins.",
    milestone4Year: "2026", milestone4Title: "New Collections", milestone4Desc: "Launched premium lightweight daily-wear gold & silver lines crafted for the modern generation.",
    value1Title: "Purity First", value1Desc: "Every piece is BIS Hallmarked, ensuring 100% certified gold and sterling silver purity standards.",
    value2Title: "Heritage Craft", value2Desc: "Three decades of passing down traditional filigree, meenakari, and stone-setting techniques through generations.",
    value3Title: "Bespoke Design", value3Desc: "Personalized jewellery consultations where your imagination and our craftsmanship unite.",
    value4Title: "Lifetime Care", value4Desc: "Complimentary polishing, sizing, and repair services to keep your jewellery looking eternally new.",
    craft1Step: "01", craft1Title: "Design & Consultation", craft1Desc: "We begin with an in-depth discussion to understand your vision, occasion, and personal style.",
    craft2Step: "02", craft2Title: "Wax Modelling", craft2Desc: "Our craftsmen create a precise wax model, allowing you to visualize the final piece.",
    craft3Step: "03", craft3Title: "Metal Casting", craft3Desc: "Using the lost-wax casting technique, your design is cast in the purest certified metals.",
    craft4Step: "04", craft4Title: "Stone Setting", craft4Desc: "Master gem-setters place each stone by hand, ensuring perfect alignment and brilliance.",
    craft5Step: "05", craft5Title: "Polish & Hallmark", craft5Desc: "The piece undergoes multi-stage polishing and receives its official BIS Hallmark certification.",
    team1Name: "Govind Devram Rane", team1Role: "Founder & Master Craftsman",
    team2Name: "Vijay Govind Rane", team2Role: "Head of Design & Gemology",
    team3Name: "Rahul Govind Rane", team3Role: "Operations & Customer Relations",
    masterJewellers: "Master Jewellers & Stylists",

    // Contact Page
    contactHeroLabel: "REACH OUT TO US",
    contactHeroTitle: "Let's Create Something",
    contactHeroTitleItalic: "Extraordinary Together",
    contactHeroSub: "Our jewellery consultants are ready to assist you with bespoke orders, wholesale enquiries, and personal consultations.",
    contactCard1Label: "Call Us",
    contactCard2Label: "Email Us",
    contactCard3Label: "Visit Us",
    contactCard4Label: "Store Hours",
    contactCard3Sub: "Hupari, Kolhapur, Maharashtra",
    contactCard4Value: "Mon – Sun",
    contactCard4Sub: "10:30 AM – 8:30 PM",
    contactFormTitle: "Send Your Enquiry",
    contactFormSub: "Tell us what you're looking for and we'll get back to you within 24 hours.",
    contactNameLabel: "Your Full Name",
    contactNamePlaceholder: "e.g. Priya Sharma",
    contactEmailLabel: "Email Address",
    contactEmailPlaceholder: "your@email.com",
    contactPhoneLabel: "Phone Number",
    contactPhonePlaceholder: "+91 98765 43210",
    contactSubjectLabel: "Subject / Enquiry Type",
    contactSubjectGeneral: "General Enquiry",
    contactSubjectWholesale: "Wholesale Order",
    contactSubjectCustom: "Custom Design",
    contactSubjectRepair: "Repair & Service",
    contactMessageLabel: "Your Message",
    contactMessagePlaceholder: "Tell us more about your requirements...",
    contactSendBtn: "Send Enquiry",
    contactSuccessTitle: "Message Sent!",
    contactSuccessDesc: "Thank you for reaching out. We'll respond within 24 hours.",
    contactWhyTitle: "Why GDR Jewellers?",
    contactWhyItem1: "BIS Certified Hallmarked 22K Gold & 925 Silver",
    contactWhyItem2: "Free same-day home delivery in Kalyan",
    contactWhyItem3: "Bespoke orders in 7–14 working days",
    contactWhyItem4: "Lifetime free polishing & sizing adjustments",
    contactMapTitle: "Find Our Showroom",
    contactMapSub: "GDR House, Rane Heritage Complex, Kalyan West, Maharashtra",
  },
  mr: {
    // Nav
    home: "मुख्य पृष्ठ",
    aboutUs: "आमच्याबद्दल",
    products: "दागिने / प्रॉडक्ट्स",
    giftsByOccasion: "गिफ्ट्स (भेटवस्तू)",
    blog: "ब्लॉग व माहिती",
    contactUs: "संपर्क साधा",
    searchPlaceholder: "दागिने शोधा...",
    cartTitle: "तुमची कार्ट",
    wishlistTitle: "आवडलेले दागिने",
    emptyCart: "तुमची कार्ट रिकामी आहे",
    emptyWishlist: "विशलिस्ट रिकामी आहे",
    subtotal: "एकूण रक्कम",
    checkoutBtn: "चौकशी करा",
    clearCart: "कार्ट रिकामी करा",
    buyNowTitle: "थेट खरेदी चौकशी",

    // Products & Filters
    filters: "फिल्टर्स",
    clearAll: "सर्व रिसेट करा",
    ringSize: "अंगठीची साईझ",
    priceRange: "किंमत श्रेणी",
    productType: "प्रॉडक्ट प्रकार",
    weightRange: "वजन श्रेणी",
    addToCart: "कार्ट मध्ये जोडा",
    buyNow: "आत्ताच खरेदी करा",
    quickView: "झटपट पहा",
    availableSizes: "उपलब्ध साईझ:",
    quantity: "प्रमाण",
    samePattern: "याच पॅटर्नचे इतर दागिने",

    // Gifting
    giftsForHer: "महिलांसाठी भेटवस्तू",
    giftsForHim: "पुरुषांसाठी भेटवस्तू",
    giftsForBaby: "लहान मुलांसाठी भेटवस्तू",
    whoShoppingFor: "तुम्ही कोणासाठी खरेदी करत आहात?",
    giftCuratorTitle: "आमचा गिफ्ट क्युरेटर वापरा!",
    giftCuratorSub: "३ सोप्या प्रश्नांची उत्तरे द्या आणि योग्य दागिना शोधा.",
    giftByOccasion: "प्रसंगानुसार भेटवस्तू",
    giftingByRelation: "नात्यानुसार भेटवस्तू",
    whoAreYouShoppingFor: "तुम्ही कोणासाठी खरेदी करत आहात?",
    selectRecipient: "त्यांच्यासाठी खास तयार केलेल्या भेटवस्तू संग्रह पाहण्यासाठी प्राप्तकर्ता निवडा",
    giftEternity: "अनंत काळाची भेटवस्तू द्या",
    giftEternityDesc: "सोने आणि चांदीचे दागिने — प्रत्येक प्रसंगाला आजीवन आठवणीत रूपांतरित करणारे",
    giftsOccasionTag: "✦ प्रसंग आणि नात्यानुसार भेटवस्तू ✦",
    specialMoments: "विशेष क्षण",
    giftByOccasionLabel: "प्रसंगानुसार भेटवस्तू",
    giftOccasionDesc: "जीवनातील सर्वात मौल्यवान क्षणांसाठी खास निवडलेले संग्रह",
    viewGifts: "भेटवस्तू पहा",
    exploreArrow: "शोधा →",
    notSureWhat: "काय खरेदी करायचे ते माहीत नाही?",
    giftCuratorHeading: "आमचा गिफ्ट क्युरेटर वापरा!",
    iAmLooking: "मी शोधत आहे उत्कृष्ट",
    chooseCategory: "श्रेणी निवडा",
    jewellery: "दागिने,",
    within: "यामध्ये",
    choosePriceRange: "किंमत श्रेणी निवडा",
    under5k: "₹५,000 पेक्षा कमी",
    range5k20k: "₹५,000 – ₹२०,०००",
    over20k: "₹२०,००० पेक्षा जास्त",
    forWord: "साठी",
    chooseRelation: "नाते निवडा",
    continueBtn: "पुढे जा",
    signaturePackaging: "सिग्नेचर पॅकेजिंग विनामूल्य",
    silverSpecial: "चांदी स्पेशल",
    goldSignature: "सोने सिग्नेचर",

    // Banner text
    gdrFineJewellery: "जीडीआर फाईन ज्वेलर्स",
    exquisiteCollection: "आमचे भव्य दागिने कलेक्शन",
    bannerDesc: "परंपरेचा आणि सौंदर्याचा अनमोल संगम",

    // Video titles
    royalCraftsmanship: "रॉयल क्राफ्ट्समॅनशिप",
    handcraftedHeritage: "हॅंडक्राफ्टेड हेरिटेज",
    silverPureElegance: "सिल्व्हर प्योर एलिगन्स",

    // Blog page
    gdrLuxuryEditorial: "जीडीआर लक्झरी एडिटोरियल",
    theGdrJournal: "जीडीआर जर्नल व एडिटोरियल",
    jewelleryStories: "दागिन्यांच्या कथा आणि परंपरेगत शैली",
    exploreCraftsmanship: "कौशल्यपूर्ण कामगिरी, सिल्व्हर केअर गाईड्स आणि फाईन ज्वेलरीमधील नवीन ट्रेंड्स शोधा",
    backToJournal: "जर्नलवर परत जा",
    enjoyedThisArticle: "आवडले हे जीडीआर जर्नल लेख?",
    subscribeText: "खासगी ज्वेलरी लॉन्च आणि स्टाईलिंग गाईड्ससाठी एक्सक्लुसिव्ह प्रिव्ह्यू आमंत्रणे मिळवण्यासाठी सबस्क्राईब करा.",
    enterEmail: "तुमचा ईमेल पत्ता टाका...",
    subscribeBtn: "सबस्क्राईब करा",
    readFullArticle: "पूर्ण लेख वाचा →",

    // Admin
    adminTitle: "जीडीआर कंट्रोल सेंटर",
    dashboard: "डॅशबोर्ड",
    totalProducts: "एकूण प्रॉडक्ट्स",
    giftItems: "गिफ्ट आयटम्स",
    articles: "ब्लॉग व लेख",
    customerInquiries: "ग्राहकांच्या चौकशी",
    addProduct: "नवीन प्रॉडक्ट जोडा",
    addGift: "नवीन गिफ्ट जोडा",
    addArticle: "नवीन लेख प्रसिद्ध करा",
    selectLanguage: "भाषा निवडा / Language",
    handcraftedHeritageAdmin: "हॅंडक्राफ्टेड हेरिटेज",
    representingPurity: "९२.५ फाईन सिल्व्हर आणि २२के रॉयल गोल्ड कलेक्शन्सची शुद्धता दर्शवत.",

    // Hero Banner
    heroBannerTag1: "✦ खास महिलांचे कलेक्शन ✦",
    heroBannerH1: "अतुलनीय सौंदर्य",
    heroBannerH2: "९२५ सिल्व्हर नेकलेस",
    heroBannerSub1: "प्रत्येक खास क्षणासाठी डिझाइन केलेले ९२५ शुद्ध चांदीचे अप्रतिम नेकलेस, पेंडंट्स आणि ब्रायडल दागिने.",
    heroBannerAccent1: "नेकलेस आणि पेंडंट्स",
    heroBannerCta1: "नेकलेस कलेक्शन पहा",
    heroBannerTag2: "✦ महाराष्ट्राचा पारंपरिक वारसा ✦",
    heroBannerH3: "पारंपरिक ९२५",
    heroBannerH4: "चांदीचे पैंजण आणि अँकलेट्स",
    heroBannerSub2: "चकाकी आणि शुद्धतेचा विश्वास देणारे अस्सल हॉलमार्क सिल्व्हर पैंजण — महाराष्ट्रातील अग्रगण्य होलसेल कलेक्शन.",
    heroBannerAccent2: "पैंजण आणि अँकलेट्स",
    heroBannerCta2: "पैंजण कलेक्शन पहा",
    heroBannerTag3: "✦ पुरुषांचे राजेशाही कलेक्शन ✦",
    heroBannerH5: "पुरुषांचे सिल्व्हर",
    heroBannerH6: "ब्रेसलेट्स आणि कडे",
    heroBannerSub3: "आकर्षक आणि भक्कम डिझाइन. आधुनिक पुरुषांसाठी बनवलेले अस्सल ९२५ सिल्व्हर ब्रेसलेट्स आणि डिझायनर कडे.",
    heroBannerAccent3: "पुरुषांची ज्वेलरी",
    heroBannerCta3: "पुरुषांचे कलेक्शन पहा",
    enquireWholesale: "होलसेल चौकशी करा",
    scrollToExplore: "शोधण्यासाठी खाली स्क्रोल करा",

    // Marquee strip
    marqueeItems: [
      "✦ शुद्ध चांदी", "✦ होलसेल", "✦ पैंजण", "✦ ब्रेसलेट्स", "✦ चेन्स",
      "✦ अंगठ्या", "✦ पुरुषांचे कलेक्शन", "✦ कंबरपट्टा", "✦ प्रीमियम गुणवत्ता",
      "✦ सर्वोत्तम किंमती", "✦ जीडीआर ज्वेलर्स"
    ],

    // Stats Section
    stat1Label: "सिल्व्हर डिझाईन्स",
    stat2Label: "वर्षांची कलाकुसर",
    stat3Label: "आनंदी ग्राहक",
    stat4Label: "शुद्ध चांदी",

    // Occasion Section
    occasionEyebrow: "✦ जीडीआर ज्वेलर्स सोबत प्रत्येक प्रसंग साजरा करा ✦",
    occasionTitle: "प्रत्येक",
    occasionTitleItalic: "खास प्रसंगासाठी",
    occasionSub: "लग्न, वाढदिवस, लग्नाचा वाढदिवस, साखरपुडा, डोहाळे जेवण आणि सणांसाठी खास तयार केलेले शुद्ध चांदीचे दागिने.",
    occasionEngagement: "साखरपुडा",
    occasionValentines: "व्हॅलेंटाईन डे",
    occasionSangeet: "संगीत",
    occasionReception: "रिसेप्शन",
    occasionWedding: "लग्नाचा दिवस",
    occasionBridal: "ब्राइडल कलेक्शन",

    // Look Section
    lookEyebrow: "✦ तुमची खास सिल्व्हर स्टाईल शोधा ✦",
    lookTitle: "तुमचा योग्य",
    lookTitleItalic: "लूक निवडा",
    lookSub: "क्लासिक, मॉडर्न, ऑफिस, पार्टी आणि इव्हिनिंग स्टाईलसाठी हस्तकलेने बनवलेले 925 चांदीचे दागिने.",
    shopTheLook: "लुक खरेदी करा",
    lookCasual: "कॅज्युअल लूक",
    lookCasualDesc: "मिनिमलिस्ट सिल्व्हर चेन आणि रोजचे स्टड्स.",
    lookOffice: "ऑफिस लूक",
    lookOfficeDesc: "रुबाबदार कफ ब्रेसलेट्स आणि साधे सौंदर्य.",
    lookModern: "मॉडर्न लूक",
    lookModernDesc: "ट्रेंडी जिओमेट्रिक आकार आणि स्टॅकेबल रिंग्ज.",
    lookClassic: "क्लासिक लूक",
    lookClassicDesc: "शाश्वत सोलिटेअर डायमंड्स आणि हेरिटेज डिझाईन्स.",
    lookParty: "पार्टी लूक",
    lookPartyDesc: "आकर्षक रत्नजडित इअररिंग्स आणि स्टेटमेंट दागिने.",
    lookEvening: "इव्हिनिंग लूक",
    lookEveningDesc: "चमकदार नेकलेस आणि ड्रॉप इअररिंग्स.",

    // New Luxury Section
    luxurySpecialGiftBox: "स्पेशल गिफ्ट बॉक्स",
    luxuryPure925: "शुद्ध 925 चांदी",
    luxurySilverIdols: "चांदीच्या मूर्ती आणि कलाकृती",
    luxurySilverIdolsDesc: "घरातील देवघर आणि शुभ भेटवस्तूंसाठी हस्तकलेच्या मूर्ती",
    luxuryExploreCollection: "कलेक्शन पहा",
    luxuryNewLaunch: "नवीन लॉन्च",
    luxuryNewArrival: "नवीन अरायवल",
    luxuryMangalsutra: "स्टेशन मंगळसूत्र आणि रॉयल चेन्स",
    luxuryMangalsutraDesc: "रोजच्या वापरासाठी आधुनिक मिनिमल 925 सिल्व्हर मंगळसूत्र",
    luxuryShopNewArrivals: "नवीन अरायव्हल्स खरेदी करा",
    luxuryGdrSignatureEdit: "जीडीआर सिग्नेचर एडिट",
    luxuryGiftsSparkle: "क्षणभरापेक्षा जास्त चमकणाऱ्या भेटवस्तू",
    luxuryGiftsDesc: "तुमच्या प्रियजनांसाठी जीडीआर मधील उत्कृष्ट हस्तकलेचे दागिने देऊन तुमचे असीम प्रेम साजरे करा.",
    luxuryExploreGifting: "जीडीआर गिफ्टिंग एडिट पहा",
    luxuryForHer: "तिच्यासाठी",
    luxuryForHerDesc: "सोलिटेअर रिंग्ज आणि चेन्स",
    luxuryForHim: "त्याच्यासाठी",
    luxuryForHimDesc: "कडा आणि क्युबन लिंक्स",
    luxuryForKids: "मुलांसाठी",
    luxuryForKidsDesc: "नाजूक पैंजण आणि नजरिया",

    // About Section (Home)
    aboutLegacyOfTrust: "✦ विश्वासाचा वारसा ✦",
    aboutTitle: "जीडीआर",
    aboutTitleItalic: "बद्दल",
    aboutTitleSuffix: "माहिती",
    aboutP1: "जीडीआर ज्वेलर्स हे होलसेल सिल्व्हर ज्वेलरी Market मधील एक प्रमुख नाव आहे. आम्ही परिसरातील रिटेलर्स आणि होलसेलर्सना उच्च-गुणवत्तेचे, शुद्ध 925 चांदीचे दागिने पुरवतो.",
    aboutP2: "आमच्या कलेक्शनमध्ये उत्कृष्ट पैंजण, सुंदर ब्रेसलेट्स, पुरुषांच्या चेन्स आणि पारंपरिक कंबरपट्ट्यांचा समावेश आहे. शुद्धता, कलाकुसर आणि विश्वासाच्या आधारावर आम्ही उत्तम हॉलमार्क गुणवत्तेची हमी देतो.",
    aboutFeature1Title: "BIS 925 हॉलमार्क",
    aboutFeature1Desc: "प्रत्येक उत्पादनावर अतुलनीय शुद्धतेची हमी.",
    aboutFeature2Title: "होलसेल स्पर्धात्मक दर",
    aboutFeature2Desc: "मोठ्या ऑर्डरसाठी अनुकूल किंमती.",
    aboutFeature3Title: "पॅन-इंडिया शिपिंग",
    aboutFeature3Desc: "सुरक्षित आणि जलद वितरण नेटवर्क.",
    aboutEst: "स्थापना 2011",

    // Footer
    footerPageHeading: "शोरूम पेजेस",
    footerGiftingHeading: "गिफ्टिंग कलेक्शन्स",
    footerInfoHeading: "शोरूम माहिती",
    footerHomeLink: "मुख्य पृष्ठ",
    footerAboutLink: "आमच्याबद्दल",
    footerProductsLink: "सर्व उत्पादने",
    footerGiftingLink: "प्रसंगानुसार गिफ्ट्स",
    footerBlogLink: "ब्लॉग व लेख",
    footerContactLink: "संपर्क साधा",
    footerGiftHer: "तिच्यासाठी गिफ्ट्स",
    footerGiftHim: "त्याच्यासाठी गिफ्ट्स",
    footerGiftBaby: "लहान मुलांसाठी",
    footerGiftBirthday: "वाढदिवसाचे गिफ्ट्स",
    footerGiftAnniversary: "अ‍ॅनिव्हर्सरी गिफ्ट्स",
    footerGiftWedding: "वेडिंग कलेक्शन",
    footerGiftValentines: "व्हॅलेंटाईन डे",
    footerAddress: "जीडीआर हाऊस, कल्याण (प), महाराष्ट्र",
    footerPhone1: "+91 91724 55555",
    footerPhone2: "+91 251 2444321",
    footerEmail: "gdrjwell@gmail.com",
    footerTerms: "नियम आणि अटी",
    footerFaq: "नेहमी विचारले जाणारे प्रश्न",
    footerBrandDesc: "उत्कृष्ट BIS हॉलमार्क केलेले 925 स्टर्लिंग चांदीचे दागिने. संपूर्ण भारतात विश्वासार्ह हस्तकलेची परंपरा.",
    footerCopyright: "जीडीआर ज्वेलर्स. सर्व हक्क राखीव.",
    footerHallmark: "BIS हॉलमार्क 925 स्टर्लिंग सिल्व्हर मास्टरपिसेस"
  }
};

// Default initial categories to seed into Firestore if empty
export const DEFAULT_CATEGORIES = [
  { name: 'Payal (Anklets)', nameMr: 'पैंजण (अँकलेट्स)', audience: 'For Women' },
  { name: 'Painjan (Anklets)', nameMr: 'पैंजण', audience: 'For Women' },
  { name: 'Chains', nameMr: 'चेन्स', audience: 'For Women' },
  { name: 'Rings', nameMr: 'अंगठ्या', audience: 'For Women' },
  { name: 'Jodvi (Toe Rings)', nameMr: 'जोडवी', audience: 'For Women' },
  { name: 'Bracelets', nameMr: 'ब्रेसलेट्स', audience: 'For Women' },
  { name: 'Mekhala (Waist Belt)', nameMr: 'मेखळा (कंबरपट्टा)', audience: 'For Women' },
  { name: 'Necklace', nameMr: 'नेकलेस', audience: 'For Women' },
  { name: 'Earrings', nameMr: 'इअररिंग्स', audience: 'For Women' },
  { name: 'Daily Wear', nameMr: 'डेली वेअर', audience: 'For Women' },
  { name: 'Chains', nameMr: 'पुरुषांच्या चेन्स', audience: 'For Men' },
  { name: 'Bracelets', nameMr: 'पुरुषांचे ब्रेसलेट', audience: 'For Men' },
  { name: 'Rings', nameMr: 'पुरुषांच्या अंगठ्या', audience: 'For Men' },
  { name: 'Kada', nameMr: 'कडा', audience: 'For Men' },
  { name: 'Kad-Dora', nameMr: 'कद-दोरा', audience: 'For Men' },
  { name: 'Painjan (Anklets)', nameMr: 'लहान मुलांचे पैंजण', audience: 'For Baby' },
  { name: 'Payal (Anklets)', nameMr: 'बेबी पैंजण', audience: 'For Baby' },
  { name: 'Tode-Vaale', nameMr: 'तोडे-वाळे', audience: 'For Baby' },
  { name: 'Mangati (Bracelets)', nameMr: 'मंगती (ब्रेसलेट्स)', audience: 'For Baby' },
  { name: 'Kad-Dora', nameMr: 'बेबी कद-दोरा', audience: 'For Baby' }
];

const INITIAL_GIFTS = [
  { id: 'gift-1', name: 'Anniversary Gold Set', nameMr: 'वाढदिवसाचा गोल्ड सेट', price: 55000, occasion: 'Anniversary', forWhom: 'For Her', category: 'Necklace', image: 'images/w chain13.jpg', description: 'A stunning gold necklace and earrings set for your anniversary beloved.', descriptionMr: 'तुमच्या वाढदिवसाच्या प्रियजनासाठी सुंदर गोल्ड नेकलेस आणि दागिने सेट.', rating: 5.0, isCombo: false },
  { id: 'gift-2', name: 'Birthday Silver Bracelet Combo', nameMr: 'वाढदिवसाचा सिल्व्हर ब्रेसलेट कॉम्बो', price: 18000, occasion: 'Birthday', forWhom: 'For Her', category: 'Bracelets', image: 'images/w br3.jpg', description: 'Birthday bundle of silver bracelet and matching ring.', descriptionMr: 'वाढदिवसाचा सिल्व्हर ब्रेसलेट आणि जुळणारी अंगठीचा बंडल.', rating: 4.8, isCombo: true },
  { id: 'gift-3', name: 'Engagement Solitaire Ring', nameMr: 'सासुर्या सोलिटेअर अंगठी', price: 42000, occasion: 'Engagement', forWhom: 'For Her', category: 'Rings', image: 'images/w ring1.jpg', description: 'Premium solitaire gold ring for the perfect proposal moment.', descriptionMr: 'परिपूर्ण प्रस्तावाच्या क्षणीसाठी प्रीमियम सोलिटेअर गोल्ड अंगठी.', rating: 5.0, isCombo: false, ringSize: '11' },
  { id: 'gift-4', name: 'Wedding Bridal Mekhala Set', nameMr: 'लग्नाची वधू मेखळा सेट', price: 95000, occasion: 'Wedding', forWhom: 'For Her', category: 'Mekhala (Waist Belt)', image: 'images/waist12.jpg', description: 'Complete bridal mekhala and payal set for the big day.', descriptionMr: 'मोठ्या दिवसासाठी पूर्ण वधू मेखळा आणि पैंजण सेट.', rating: 5.0, isCombo: true },
  { id: 'gift-5', name: 'Men\'s Gold Kada Gift', nameMr: 'पुरुषांसाठी गोल्ड कडा गिफ्ट', price: 32000, occasion: 'Anniversary', forWhom: 'For Him', category: 'Bracelets', image: 'images/men br6.jpg', description: 'Premium gold kada bracelet for the man of strength.', descriptionMr: 'शक्तिशाली पुरुषासाठी प्रीमियम गोल्ड कडा ब्रेसलेट.', rating: 4.9, isCombo: false },
  { id: 'gift-6', name: 'Men\'s Chain Bundle', nameMr: 'पुरुषांसाठी चेन बंडल', price: 28000, occasion: 'Birthday', forWhom: 'For Him', category: 'Chains', image: 'images/men chain10.jpg', description: 'Classic curb chain with matching ring — a gentleman\'s combo.', descriptionMr: 'जुळणारी अंगठीसह क्लासिक कर्ब चेन — एक जेंटलमन कॉम्बो.', rating: 4.8, isCombo: true },
  { id: 'gift-7', name: 'Baby Painjan Silver Set', nameMr: 'बेबी पैंजण सिल्व्हर सेट', price: 4500, occasion: 'Baby Shower', forWhom: 'For Baby', category: 'Painjan (Anklets)', image: 'images/painjan5.jpg', description: 'Soft silver painjan set for the new arrival.', descriptionMr: 'नवीन आगमनासाठी मऊत सिल्व्हर पैंजण सेट.', rating: 4.9, isCombo: false },
  { id: 'gift-8', name: 'Baby Welcome Combo', nameMr: 'बेबी वेलकम कॉम्बो', price: 7800, occasion: 'Baby Shower', forWhom: 'For Baby', category: 'Painjan (Anklets)', image: 'images/painjan7.jpg', description: 'Baby anklets + bracelet combo as a beautiful welcome gift.', descriptionMr: 'सुंदर स्वागत भेटवस्तू म्हणून बेबी अँकलेट्स + ब्रेसलेट कॉम्बो.', rating: 5.0, isCombo: true },
  { id: 'gift-9', name: 'Valentine\'s Heart Diamond Ring', nameMr: 'व्हॅलेनटाईन हार्ट डायमंड अंगठी', price: 35000, occasion: 'Valentine\'s Day', forWhom: 'For Her', category: 'Rings', image: 'images/w ring14.jpg', description: 'Romantic heart-cut diamond ring for your special Valentine.', descriptionMr: 'तुमच्या विशेष व्हॅलेनटाईनसाठी रोमँटिक हार्ट-कट डायमंड अंगठी.', rating: 5.0, isCombo: false, ringSize: '12' }
];

const INITIAL_BLOGS = [
  {
    id: 'blog-1',
    title: 'The Eternal Charm of Silver Payals',
    titleMr: 'सिल्व्हर पैंजणांचे शाश्वत आकर्षण',
    excerpt: 'Explore the rich history, cultural significance, and modern styling of traditional payals.',
    excerptMr: 'पारंपरिक पैंजणांचा समृद्ध इतिहास, सांस्कृतिक महत्त्व आणि आधुनिक स्टाईलिंग शोधा.',
    content: 'Silver anklets, commonly known as Payals or Painjans, hold a very special place in Indian households. Far beyond just an ornament, they are symbols of grace, traditional values, and are believed to bring positive energy into the home. Handcrafted silver anklets are versatile accessories that complement both classic ethnic wear and contemporary outfits. In this guide, we dive deep into how you can choose the perfect weight, length, and ghungroo pattern for your occasion.',
    contentMr: 'सिल्व्हर अँकलेट्स, ज्यांना सामान्यतः पैंजण किंवा पैंजण म्हणून ओळखले जाते, भारतीय घरांमध्ये अतिशय विशेष स्थान आहे. फक्त एक अलंकार होण्यापलीकडे, ते अनुग्रह, पारंपरिक मूल्यांचे प्रतीक आहेत आणि घरात सकारात्मक ऊर्जा आणतात असे मानले जाते. हस्तकला केलेल्या सिल्व्हर अँकलेट्स हे बहुमुखी अॅक्सेसरीज आहेत जे क्लासिक एथनिक वेअर आणि समकालीन आउटफिट्स दोन्हींशी जुळतात. या मार्गदर्शिकामध्ये, आपण तुमच्या प्रसंगासाठी परिपूर्ण वजन, लांबी आणि घुंगरू पॅटर्न कसे निवडायचे हे आपण खोलवर शोधू.',
    image: 'images/painjan10.jpg',
    author: 'GDR Editorial',
    date: 'August 01, 2026',
    readTime: '4 min read'
  },
  {
    id: 'blog-2',
    title: 'Bold and Handsome: Jewellery Trends for Men',
    titleMr: 'बोल्ड आणि हँडसम: पुरुषांसाठी ज्वेलरी ट्रेंड्स',
    excerpt: 'From Cuban bracelets to statement rings, men\'s jewellery is undergoing a major renaissance.',
    excerptMr: 'क्युबन ब्रेसलेटपासून स्टेटमेंट रिंग्सपर्यंत, पुरुषांची ज्वेलरी मोठ्या पुनरुज्जीवनातून जात आहे.',
    content: 'Modern fashion is breaking old stereotypes, and men\'s jewellery is experiencing an unprecedented surge. Today\'s man isn\'t afraid to express his style with textured Kada bands, sleek platinum-plated chains, and bold stone rings like black onyx and tiger\'s eye. The secret to styling men\'s jewelry is keeping it balanced, selecting one or two focal points (like a bold ring or a robust bracelet), and matching metal finishes to the rest of the outfit.',
    contentMr: 'आधुनिक फॅशन जुन्या स्टिरिओटाइप्स तोडत आहे आणि पुरुषांची ज्वेलरी अभूतपूर्वी वाढीस आली आहे. आजचा माणूस टेक्स्चर्ड कडा बँड्स, स्लीक प्लॅटिनम-प्लेटेड चेन्स आणि ब्लॅक ओनिक्स आणि टायगर आय सारख्या बोल्ड स्टोन रिंग्ससह त्याची शैली व्यक्त करण्यासाठी भीडत नाही. पुरुषांच्या ज्वेलरीचे स्टाईलिंग करण्याचे रहस्य म्हणजे ते संतुलित ठेवणे, एक किंवा दोन फोकल पॉइंट्स (जसे की बोल्ड रिंग किंवा मजबूत ब्रेसलेट) निवडणे आणि आउटफिटच्या इतर भागांशी मेटल फिनिश जुळवणे.',
    image: 'images/men ring10.jpg',
    author: 'Vijay Shinde',
    date: 'July 28, 2026',
    readTime: '6 min read'
  },
  {
    id: 'blog-3',
    title: 'Gifting Jewellery: The Ultimate Guide',
    titleMr: 'ज्वेलरी भेटवस्तू: अल्टिमेट गाईड',
    excerpt: 'Make your special moments unforgettable with the timeless gift of fine jewellery.',
    excerptMr: 'फाईन ज्वेलरीच्या कालातीत भेटवस्तूंसह तुमचे विशेष क्षण अविस्मरणीय करा.',
    content: 'Whether it\'s an anniversary, a milestone birthday, or welcoming a new baby, jewellery remains the ultimate expression of love and care. When selecting a gift, consider the recipient\'s style. Do they prefer delicate rose gold chain bracelets or bold heritage necklaces? For babies, lightweight pure silver anklets (Painjans) or bracelets (Mangati) with smooth edges are perfect and hold lasting sentimental value.',
    contentMr: 'हे वाढदिवस असो, महत्त्वपूर्ण वाढदिवस किंवा नवीन बाळाचे स्वागत करणे असो, ज्वेलरी प्रेम आणि काळजीचे अल्टिमेट अभिव्यक्ती आहे. भेटवस्तू निवडताना, स्वीकारकाची शैली विचारात घ्या. त्यांना नाजूक रोझ गोल्ड चेन ब्रेसलेट्स आणि बोल्ड हेरिटेज नेकलेस्स आवडतात का? बाळांसाठी, हलक्या शुद्ध चांदीच्या पैंजण (पैंजण) किंवा मुलायम एजससह ब्रेसलेट्स (मांगटी) परिपूर्ण आहेत आणि त्यांना कायमस्वरूपी संवेदनशील मूल्य आहे.',
    image: 'images/w br11.jpg',
    author: 'Preeti Deshmukh',
    date: 'July 15, 2026',
    readTime: '5 min read'
  }
];

const INITIAL_TESTIMONIALS = [
  { id: 't1', img: '/Images/men ring2.jpg', name: 'Rajesh Sharma', role: 'Jeweller, Mumbai', roleMr: 'ज्वेलर, मुंबई', text: 'GDR Jewellers provides the highest purity silver ornaments. Their wholesale pricing and delivery speed are unparalleled.', textMr: 'जीडीआर ज्वेलर्स सर्वोच्च शुद्धतेचे चांदीचे दागिने पुरवतात. त्यांची होलसेल किंमत आणि डिलिव्हरी स्पीड अतुलनीय आहे.' },
  { id: 't2', img: '/Images/painjan10.jpg', name: 'Anita Patel', role: 'Retailer, Ahmedabad', roleMr: 'रिटेलर, Ahmedabad', text: 'The Payal & Anklet range is extremely popular with our customers. The finishing on the silver is flawless!', textMr: 'पैंजण आणि अँकलेट रेंज आमच्या ग्राहकांमध्ये खूप लोकप्रिय आहे. चांदीवरील फिनिशिंग अप्रतिम आहे!' },
  { id: 't3', img: '/Images/w br14.jpg', name: 'Vikram Mehta', role: 'Wholesaler, Delhi', roleMr: 'होलसेलर, दिल्ली', text: "Excellent catalog selections. The men's chain series and custom cuff bracelets sell out immediately in our stores.", textMr: 'उत्कृष्ट कॅटलॉग निवड. पुरुषांची चेन सीरिज आणि कस्टम कफ ब्रेसलेट आमच्या दुकानात लगेच विकले जातात.' },
  { id: 't4', img: '/Images/w ring1.jpg', name: 'Priyanka Sen', role: 'Boutique Owner, Kolkata', roleMr: 'बुटीक ओनर, कोलकाता', text: 'Highly recommend their hallmarked collection. Customer support answers enquiries fast and accommodates custom sizes.', textMr: 'त्यांच्या हॉलमार्क कलेक्शनची मी जोरदार शिफारस करते. ग्राहक सेवा चौकशींना लवकर उत्तर देते आणि कस्टम साईझ सामावून घेते.' },
  { id: 't5', img: '/Images/painjan19.jpg', name: 'Devendra Joshi', role: 'Showroom Owner, Jaipur', roleMr: 'शोरूम ओनर, जयपूर', text: 'Stunning designs and consistent 92.5 purity. The wholesale ordering flow is very transparent and reliable.', textMr: 'अप्रतिम डिझाईन्स आणि सातत्यपूर्ण 92.5 शुद्धता. होलसेल ऑर्डरिंग प्रक्रिया अतिशय पारदर्शक आणि विश्वासार्ह आहे.' },
  { id: 't6', img: '/Images/men br8.jpg', name: 'Siddharth Roy', role: 'Distributor, Chennai', roleMr: 'डिस्ट्रिब्युटर, चेन्नई', text: 'Their range of heavy silver bracelets and kada styles has helped us grow our retail customer base significantly.', textMr: 'त्यांच्या जड चांदीच्या ब्रेसलेट आणि कडा स्टाईल्सच्या रेंजमुळे आम्हाला आमचा रिटेल ग्राहक आधार लक्षणीयरीत्या वाढवता आला.' },
  { id: 't7', img: '/Images/w ring2.jpg', name: 'Meenakshi Iyer', role: 'Boutique Designer, Bangalore', roleMr: 'बुटीक डिझायनर, बंगळुरू', text: 'The artisan craftsmanship on the custom waist chains is highly detailed. A solid partnership for our brand.', textMr: 'कस्टम कंबरपट्ट्यांवरील कारागीर कलाकुसर अत्यंत सूक्ष्म आहे. आमच्या ब्रँडसाठी एक भक्कम भागीदारी.' }
];

const INITIAL_FAQS = [
  {
    id: 'faq-1',
    question: 'Are all GDR jewellery pieces BIS hallmarked?',
    questionMr: 'सर्व जीडीआर दागिन्यांवर बीआयएस (BIS) हॉलमार्क आहे का?',
    answer: 'Yes! Every 925 sterling silver article sold by GDR Jewellers is BIS hallmarked and backed by purity certification.',
    answerMr: 'होय! जीडीआर ज्वेलर्सद्वारे विकले जाणारे प्रत्येक ९२५ स्टर्लिंग चांदीचे उत्पादन बीआयएस (BIS) हॉलमार्क केलेले असते आणि त्यासोबत शुद्धतेचे प्रमाणपत्र असते.'
  },
  {
    id: 'faq-2',
    question: 'How do I choose the right ring size?',
    questionMr: 'मी अंगठीचा योग्य आकार कसा निवडू?',
    answer: 'We support sizes from 1 to 30. You can visit our showroom or use the sizing guide on product pages for the closest match.',
    answerMr: 'आम्ही १ ते ३० आकाराचे समर्थन करतो. आपण आमच्या शोरूमला भेट देऊ शकता किंवा जवळच्या आकारासाठी उत्पादन पृष्ठावरील आकार मार्गदर्शिका वापरू शकता.'
  },
  {
    id: 'faq-3',
    question: 'Can I place a custom order?',
    questionMr: 'मी सानुकूल (कस्टम) ऑर्डर देऊ शकतो का?',
    answer: 'Absolutely. Our design team creates custom silver payal, gifting, and traditional ornaments based on your requirement.',
    answerMr: 'नक्कीच. आमची डिझाइन टीम तुमच्या गरजेनुसार सानुकूल चांदीचे पैंजण, भेटवस्तू आणि पारंपारिक दागिने तयार करते.'
  }
];

const INITIAL_TERMS = [
  {
    id: 'term-1',
    title: 'BIS Hallmark & Purity Certification',
    titleMr: 'बीआयएस (BIS) हॉलमार्क आणि शुद्धता प्रमाणपत्र',
    description: 'Every 925 sterling silver item sold by GDR Jewellers is certified by BIS with official hallmarking and purity markings.',
    descriptionMr: 'जीडीआर ज्वेलर्सद्वारे विकले जाणारे प्रत्येक ९२५ स्टर्लिंग चांदीचे उत्पादन अधिकृत हॉलमार्किंग आणि शुद्धतेच्या खुणांसह बीआयएस (BIS) द्वारे प्रमाणित आहे.'
  },
  {
    id: 'term-2',
    title: 'Pricing & Transparency',
    titleMr: 'किंमत आणि पारदर्शकता',
    description: 'Prices are calculated based on live silver market rates, making charges are shown clearly on the final invoice.',
    descriptionMr: 'किंमती चांदीच्या थेट बाजार भावावर आधारित मोजल्या जातात, मजुरीचे शुल्क अंतिम इनव्हॉइसवर स्पष्टपणे दर्शविले जाते.'
  },
  {
    id: 'term-3',
    title: 'Custom Orders & Ring Sizing',
    titleMr: 'सानुकूल ऑर्डर आणि अंगठीचे आकार',
    description: 'Custom designs and ring sizing require a confirmation deposit and are crafted as per the customer-specified measurements.',
    descriptionMr: 'सानुकूल डिझाइन आणि अंगठीच्या आकारासाठी पुष्टीकरण ठेव आवश्यक आहे आणि ग्राहकाने दिलेल्या मोजमापांनुसार ते तयार केले जाते.'
  }
];

export const AppProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gifts, setGifts] = useState(INITIAL_GIFTS);
  const [blogs, setBlogs] = useState(INITIAL_BLOGS);
  const [testimonials, setTestimonials] = useState(INITIAL_TESTIMONIALS);
  const [faqEntries, setFaqEntries] = useState(INITIAL_FAQS);
  const [termsEntries, setTermsEntries] = useState(INITIAL_TERMS);

  // Language State: 'en' or 'mr'
  const [lang, setLang] = useState(() => {
    const savedLang = localStorage.getItem('gdr_lang') || 'en';
    // Apply body class & lang attribute immediately on init
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('lang-mr', savedLang === 'mr');
      document.body.classList.toggle('lang-en', savedLang === 'en');
      document.body.setAttribute('lang', savedLang);
      if (document.documentElement) document.documentElement.setAttribute('lang', savedLang);
    }
    return savedLang;
  });

  const toggleLanguage = (selectedLang) => {
    const nextLang = selectedLang || (lang === 'en' ? 'mr' : 'en');
    setLang(nextLang);
    localStorage.setItem('gdr_lang', nextLang);
    // Apply body class & lang attribute for Devanagari font switching
    if (typeof document !== 'undefined') {
      document.body.setAttribute('lang', nextLang);
      if (document.documentElement) document.documentElement.setAttribute('lang', nextLang);
      if (nextLang === 'mr') {
        document.body.classList.add('lang-mr');
        document.body.classList.remove('lang-en');
      } else {
        document.body.classList.remove('lang-mr');
        document.body.classList.add('lang-en');
      }
    }
    // Sync i18n library language
    try { i18n.changeLanguage(nextLang); } catch (e) { }
  };

  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  const [cart, setCart] = useState(() => {
    const localCart = localStorage.getItem('gdr_cart');
    return localCart ? JSON.parse(localCart) : [];
  });
  const [wishlist, setWishlist] = useState(() => {
    const localWishlist = localStorage.getItem('gdr_wishlist');
    return localWishlist ? JSON.parse(localWishlist) : [];
  });
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(() => {
    try {
      const local = localStorage.getItem('gdr_stats');
      if (local) return JSON.parse(local);
    } catch (e) {}
    return DEFAULT_STATS;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem('gdr_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('gdr_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);


  useEffect(() => {
    let unsubProducts, unsubBlogs, unsubInquiries, unsubGifts, unsubCategories;

    try {
      unsubProducts = onSnapshot(collection(db, 'products'), (snapshot) => {
        if (!snapshot.empty) {
          const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setProducts(list);
        } else {
          setProducts([]);
        }
      }, (err) => {
        console.warn("Firestore products onSnapshot error:", err);
        setProducts([]);
      });

      unsubCategories = onSnapshot(collection(db, 'categories'), async (snapshot) => {
        if (!snapshot.empty) {
          const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setCategories(list);
        } else {
          // If Firestore categories collection is empty and never seeded, seed with DEFAULT_CATEGORIES
          if (!localStorage.getItem('gdr_categories_seeded')) {
            localStorage.setItem('gdr_categories_seeded', 'true');
            try {
              const seedPromises = DEFAULT_CATEGORIES.map(cat =>
                addDoc(collection(db, 'categories'), {
                  ...cat,
                  createdAt: new Date().toISOString()
                })
              );
              await Promise.all(seedPromises);
            } catch (seedErr) {
              console.warn("Could not seed categories to Firestore:", seedErr);
              setCategories(DEFAULT_CATEGORIES.map((cat, i) => ({ id: 'seed-' + i, ...cat })));
            }
          } else {
            setCategories([]);
          }
        }
      }, (err) => {
        console.warn("Firestore categories onSnapshot error:", err);
      });

      unsubGifts = onSnapshot(collection(db, 'gifts'), (snapshot) => {
        if (!snapshot.empty) {
          const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setGifts(list.length > 0 ? list : INITIAL_GIFTS);
        } else {
          setGifts(INITIAL_GIFTS);
        }
      }, (err) => {
        console.warn("Firestore gifts onSnapshot error, using fallback:", err);
        setGifts(INITIAL_GIFTS);
      });

      unsubBlogs = onSnapshot(collection(db, 'blogs'), (snapshot) => {
        if (!snapshot.empty) {
          const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          setBlogs(list.length > 0 ? list : INITIAL_BLOGS);
        } else {
          setBlogs(INITIAL_BLOGS);
        }
      }, (err) => {
        console.warn("Firestore blogs onSnapshot error, using fallback:", err);
        setBlogs(INITIAL_BLOGS);
      });

      unsubInquiries = onSnapshot(collection(db, 'inquiries'), (snapshot) => {
        setInquiries(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      }, () => { });

      // Testimonials listener
      onSnapshot(collection(db, 'testimonials'), (snapshot) => {
        if (!snapshot.empty) {
          setTestimonials(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } else {
          INITIAL_TESTIMONIALS.forEach(async (t) => {
            await setDoc(doc(db, 'testimonials', t.id), t);
          });
          setTestimonials(INITIAL_TESTIMONIALS);
        }
      }, () => { });

      // FAQ listener
      onSnapshot(collection(db, 'faqs'), (snapshot) => {
        if (!snapshot.empty) {
          setFaqEntries(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } else {
          INITIAL_FAQS.forEach(async (f) => {
            await setDoc(doc(db, 'faqs', f.id), f);
          });
          setFaqEntries(INITIAL_FAQS);
        }
      }, () => { });

      // Terms listener
      onSnapshot(collection(db, 'terms'), (snapshot) => {
        if (!snapshot.empty) {
          setTermsEntries(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } else {
          INITIAL_TERMS.forEach(async (t) => {
            await setDoc(doc(db, 'terms', t.id), t);
          });
          setTermsEntries(INITIAL_TERMS);
        }
      }, () => { });

      // Stats listener
      onSnapshot(collection(db, 'settings'), (snapshot) => {
        const statsDoc = snapshot.docs.find(d => d.id === 'stats');
        if (statsDoc && statsDoc.data()?.statsList) {
          const list = statsDoc.data().statsList;
          setStats(list);
          localStorage.setItem('gdr_stats', JSON.stringify(list));
        } else {
          setDoc(doc(db, 'settings', 'stats'), { statsList: DEFAULT_STATS });
        }
      }, () => { });

      setLoading(false);
    } catch (e) {
      setLoading(false);
    }

    return () => {
      if (unsubProducts) unsubProducts();
      if (unsubCategories) unsubCategories();
      if (unsubGifts) unsubGifts();
      if (unsubBlogs) unsubBlogs();
      if (unsubInquiries) unsubInquiries();
    };
  }, []);

  // Amazon-style cart logic: Support multiple items & size variations
  const addToCart = (product, quantity = 1, selectedSize = '') => {
    setCart(prevCart => {
      const itemKey = `${product.id}-${selectedSize || 'std'}`;
      const existingIndex = prevCart.findIndex(item => item.itemKey === itemKey || (item.product.id === product.id && item.selectedSize === selectedSize));

      if (existingIndex > -1) {
        const updated = [...prevCart];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }

      return [...prevCart, {
        itemKey,
        product,
        quantity,
        selectedSize: selectedSize || product.selectedSize || ''
      }];
    });
  };

  const removeFromCart = (itemKeyOrId) => {
    setCart(prev => prev.filter(item => item.itemKey !== itemKeyOrId && item.product.id !== itemKeyOrId));
  };

  const updateCartQuantity = (itemKeyOrId, quantity) => {
    if (quantity <= 0) { removeFromCart(itemKeyOrId); return; }
    setCart(prev => prev.map(item => (item.itemKey === itemKeyOrId || item.product.id === itemKeyOrId) ? { ...item, quantity } : item));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) => {
    setWishlist(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const addProduct = async (newProduct) => {
    const dataWithTimestamp = {
      ...newProduct,
      createdAt: newProduct.createdAt || new Date().toISOString()
    };
    try {
      const docRef = await addDoc(collection(db, 'products'), dataWithTimestamp);
      return docRef.id;
    } catch (e) {
      const id = 'prod-' + Date.now();
      setProducts(prev => [{ id, ...dataWithTimestamp }, ...prev]);
      return id;
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await deleteDoc(doc(db, 'products', productId));
    } catch (e) {
      setProducts(prev => prev.filter(p => p.id !== productId));
    }
  };

  const addGift = async (newGift) => {
    const dataWithTimestamp = {
      ...newGift,
      createdAt: newGift.createdAt || new Date().toISOString()
    };
    try {
      const docRef = await addDoc(collection(db, 'gifts'), dataWithTimestamp);
      return docRef.id;
    } catch (e) {
      const id = 'gift-' + Date.now();
      setGifts(prev => [{ id, ...dataWithTimestamp }, ...prev]);
      return id;
    }
  };

  const deleteGift = async (giftId) => {
    try {
      await deleteDoc(doc(db, 'gifts', giftId));
    } catch (e) {
      setGifts(prev => prev.filter(g => g.id !== giftId));
    }
  };

  const addBlog = async (newBlog) => {
    const dataWithTimestamp = {
      ...newBlog,
      createdAt: newBlog.createdAt || new Date().toISOString()
    };
    try {
      const docRef = await addDoc(collection(db, 'blogs'), dataWithTimestamp);
      return docRef.id;
    } catch (e) {
      const id = 'blog-' + Date.now();
      setBlogs(prev => [{ id, ...dataWithTimestamp }, ...prev]);
      return id;
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      await deleteDoc(doc(db, 'blogs', blogId));
    } catch (e) {
      setBlogs(prev => prev.filter(b => b.id !== blogId));
    }
  };

  const addInquiry = async (inquiryData) => {
    try {
      await addDoc(collection(db, 'inquiries'), {
        ...inquiryData,
        createdAt: new Date().toISOString()
      });
    } catch (e) {
      setInquiries(prev => [...prev, { id: 'inq-' + Date.now(), ...inquiryData, createdAt: new Date().toISOString() }]);
    }
  };

  const deleteInquiry = async (inquiryId) => {
    try {
      await deleteDoc(doc(db, 'inquiries', inquiryId));
    } catch (e) {
      setInquiries(prev => prev.filter(i => i.id !== inquiryId));
    }
  };

  const updateInquiry = async (inquiryId, updatedData) => {
    try {
      await setDoc(doc(db, 'inquiries', inquiryId), updatedData, { merge: true });
    } catch (e) {
      console.error("Error updating inquiry in Firestore:", e);
      setInquiries(prev => prev.map(i => i.id === inquiryId ? { ...i, ...updatedData } : i));
    }
  };

  const updateProduct = async (productId, updatedData) => {
    try {
      await setDoc(doc(db, 'products', productId), updatedData, { merge: true });
    } catch (e) {
      console.error("Error updating product in Firestore:", e);
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updatedData } : p));
    }
  };

  const addCategory = async (newCategory) => {
    const dataWithTimestamp = {
      name: newCategory.name ? newCategory.name.trim() : '',
      nameMr: newCategory.nameMr ? newCategory.nameMr.trim() : '',
      audience: newCategory.audience || 'For Women',
      createdAt: newCategory.createdAt || new Date().toISOString()
    };
    try {
      const docRef = await addDoc(collection(db, 'categories'), dataWithTimestamp);
      return docRef.id;
    } catch (e) {
      console.error("Error adding category to Firestore:", e);
      const id = 'cat-' + Date.now();
      setCategories(prev => [...prev, { id, ...dataWithTimestamp }]);
      return id;
    }
  };

  const updateCategory = async (categoryId, updatedData) => {
    try {
      await setDoc(doc(db, 'categories', categoryId), updatedData, { merge: true });
    } catch (e) {
      console.error("Error updating category in Firestore:", e);
      setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, ...updatedData } : c));
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
    } catch (e) {
      console.error("Error deleting category in Firestore:", e);
      setCategories(prev => prev.filter(c => c.id !== categoryId));
    }
  };

  const updateGift = async (giftId, updatedData) => {
    try {
      await setDoc(doc(db, 'gifts', giftId), updatedData, { merge: true });
    } catch (e) {
      console.error("Error updating gift in Firestore:", e);
      setGifts(prev => prev.map(g => g.id === giftId ? { ...g, ...updatedData } : g));
    }
  };

  const updateBlog = async (blogId, updatedData) => {
    try {
      await setDoc(doc(db, 'blogs', blogId), updatedData, { merge: true });
    } catch (e) {
      console.error("Error updating blog in Firestore:", e);
      setBlogs(prev => prev.map(b => b.id === blogId ? { ...b, ...updatedData } : b));
    }
  };

  const addTestimonial = async (data) => {
    try {
      const docRef = await addDoc(collection(db, 'testimonials'), data);
      return docRef.id;
    } catch (e) {
      const id = 'test-' + Date.now();
      setTestimonials(prev => [...prev, { id, ...data }]);
      return id;
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await deleteDoc(doc(db, 'testimonials', id));
    } catch (e) {
      setTestimonials(prev => prev.filter(t => t.id !== id));
    }
  };

  const updateTestimonial = async (id, updatedData) => {
    try {
      await updateDoc(doc(db, 'testimonials', id), updatedData);
    } catch (e) {
      setTestimonials(prev => prev.map(t => t.id === id ? { ...t, ...updatedData } : t));
    }
  };

  const addFaqEntry = async (item) => {
    try {
      await addDoc(collection(db, 'faqs'), item);
      // onSnapshot listener will automatically update faqEntries state
    } catch (e) {
      console.error('Failed to add FAQ to Firebase:', e);
      // Fallback: update state locally so UI still responds
      const newItem = { id: `faq-${Date.now()}`, ...item };
      setFaqEntries(prev => [...prev, newItem]);
    }
  };

  const updateFaqEntry = async (id, item) => {
    try {
      await updateDoc(doc(db, 'faqs', id), item);
    } catch (e) { }
    setFaqEntries(prev => prev.map(entry => entry.id === id ? { ...entry, ...item } : entry));
  };

  const deleteFaqEntry = async (id) => {
    try {
      await deleteDoc(doc(db, 'faqs', id));
    } catch (e) { }
    setFaqEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const addTermCard = async (item) => {
    try {
      await addDoc(collection(db, 'terms'), item);
      // onSnapshot listener will automatically update termsEntries state
    } catch (e) {
      console.error('Failed to add Term to Firebase:', e);
      // Fallback: update state locally so UI still responds
      const newItem = { id: `term-${Date.now()}`, ...item };
      setTermsEntries(prev => [...prev, newItem]);
    }
  };

  const updateTermCard = async (id, item) => {
    try {
      await updateDoc(doc(db, 'terms', id), item);
    } catch (e) { }
    setTermsEntries(prev => prev.map(entry => entry.id === id ? { ...entry, ...item } : entry));
  };

  const deleteTermCard = async (id) => {
    try {
      await deleteDoc(doc(db, 'terms', id));
    } catch (e) { }
    setTermsEntries(prev => prev.filter(entry => entry.id !== id));
  };

  // Clear all demo/static data function
  const clearAllData = async () => {
    try {
      const pSnap = await getDocs(collection(db, 'products')).catch(() => ({ docs: [] }));
      for (const d of pSnap.docs) {
        await deleteDoc(doc(db, 'products', d.id)).catch(() => {});
      }
      setProducts([]);

      const gSnap = await getDocs(collection(db, 'gifts')).catch(() => ({ docs: [] }));
      for (const d of gSnap.docs) {
        await deleteDoc(doc(db, 'gifts', d.id)).catch(() => {});
      }
      setGifts([]);

      const bSnap = await getDocs(collection(db, 'blogs')).catch(() => ({ docs: [] }));
      for (const d of bSnap.docs) {
        await deleteDoc(doc(db, 'blogs', d.id)).catch(() => {});
      }
      setBlogs([]);

      const iSnap = await getDocs(collection(db, 'inquiries')).catch(() => ({ docs: [] }));
      for (const d of iSnap.docs) {
        await deleteDoc(doc(db, 'inquiries', d.id)).catch(() => {});
      }
      setInquiries([]);
    } catch (e) {
      console.error('Error clearing data:', e);
    }
  };

  const updateStats = async (newStatsList) => {
    setStats(newStatsList);
    try {
      localStorage.setItem('gdr_stats', JSON.stringify(newStatsList));
      await setDoc(doc(db, 'settings', 'stats'), { statsList: newStatsList });
    } catch (e) {
      console.warn('Error saving stats to Firestore:', e);
    }
  };

  const resetStats = async () => {
    setStats(DEFAULT_STATS);
    try {
      localStorage.setItem('gdr_stats', JSON.stringify(DEFAULT_STATS));
      await setDoc(doc(db, 'settings', 'stats'), { statsList: DEFAULT_STATS });
    } catch (e) {
      console.warn('Error resetting stats in Firestore:', e);
    }
  };

  return (
    <AppContext.Provider value={{
      products,
      gifts,
      blogs,
      testimonials,
      cart,
      wishlist,
      inquiries,
      loading,
      lang,
      t,
      faqEntries,
      termsEntries,
      toggleLanguage,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      toggleWishlist,
      addProduct,
      deleteProduct,
      updateProduct,
      categories,
      addCategory,
      updateCategory,
      deleteCategory,
      DEFAULT_CATEGORIES,
      addGift,
      deleteGift,
      updateGift,
      addBlog,
      deleteBlog,
      updateBlog,
      addTestimonial,
      deleteTestimonial,
      updateTestimonial,
      addInquiry,
      deleteInquiry,
      updateInquiry,
      addFaqEntry,
      updateFaqEntry,
      deleteFaqEntry,
      addTermCard,
      updateTermCard,
      deleteTermCard,
      stats,
      updateStats,
      resetStats,
      DEFAULT_STATS,
      clearAllData
    }}>
      {children}
    </AppContext.Provider>
  );
};
